import { useCallback, useEffect, useState } from "react";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RESULT = { success: ["green", "Successo"], failure: ["red", "Fallito"], denied: ["amber", "Negato"] };
const ALL = "__all";

const Filters = ({ f, setF, actions }) => (
  <div className="mb-4 flex flex-col gap-3 sm:flex-row">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <Input className="pl-9" placeholder="Filtra per utente o record…" maxLength={100} value={f.q}
        onChange={(e) => setF({ ...f, q: e.target.value, page: 1 })} data-testid="audit-filter-search" />
    </div>
    <Select value={f.action || ALL} onValueChange={(v) => setF({ ...f, action: v === ALL ? "" : v, page: 1 })}>
      <SelectTrigger className="sm:w-56" data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>Tutte le azioni</SelectItem>{actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
    </Select>
    <Select value={f.result || ALL} onValueChange={(v) => setF({ ...f, result: v === ALL ? "" : v, page: 1 })}>
      <SelectTrigger className="sm:w-44" data-testid="audit-filter-result"><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>Tutti gli esiti</SelectItem>{Object.entries(RESULT).map(([k, [, l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const params = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null));

export default function AdminAudit() {
  const { can } = useAuth();
  const [f, setF] = useState({ q: "", action: "", result: "", page: 1 });
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => api.get("/admin/audit", { params: params(f) }).then((r) => setData(r.data)).catch((e) => toast.error(errorMessage(e))), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = async () => {
    try {
      const { page, ...rest } = f;
      const r = await api.get("/admin/audit/export", { params: params(rest), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: "onsuite-audit-log.csv" });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Esportazione completata");
    } catch (e) { toast.error("Esportazione non riuscita"); }
  };

  return (
    <div data-testid="admin-audit-page">
      <PageHeader overline="Amministrazione" title="Audit Log" description="Registro immutabile delle operazioni. Nessun token, secret o password viene registrato."
        actions={can("audit:export") && <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="audit-export-button"><Download className="h-4 w-4" />Esporta CSV</Button>} />
      <Filters f={f} setF={setF} actions={data?.actions || []} />
      <Panel className="overflow-x-auto">
        <Table data-testid="admin-audit-log-table">
          <TableHeader><TableRow>
            <TableHead>Data e ora</TableHead><TableHead>Utente</TableHead><TableHead>Azione</TableHead>
            <TableHead>Entità</TableHead><TableHead>Esito</TableHead><TableHead>Origine</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((e) => (
              <TableRow key={e.id} className="cursor-pointer" onClick={() => setDetail(e)} data-testid={`audit-row-${e.id}`}>
                <TableCell className="whitespace-nowrap font-mono text-xs text-slate-600">{fmtDateTime(e.timestamp)}</TableCell>
                <TableCell><p className="text-sm text-slate-800">{e.user_name || "—"}</p><p className="text-xs text-slate-500">{e.user_email || "Sistema"}</p></TableCell>
                <TableCell><span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-700">{e.action}</span></TableCell>
                <TableCell className="text-xs text-slate-600">{e.entity}</TableCell>
                <TableCell><StatusBadge tone={RESULT[e.result][0]}>{RESULT[e.result][1]}</StatusBadge></TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{e.origin?.ip || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.items.length === 0 && <p className="p-6 text-sm text-slate-500" data-testid="audit-empty">Nessun evento trovato.</p>}
      </Panel>
      {data && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span data-testid="audit-total">{data.total} eventi</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={f.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="audit-prev-page">Precedente</Button>
            <span>{data.page} / {data.pages}</span>
            <Button size="sm" variant="outline" disabled={f.page >= data.pages} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="audit-next-page">Successiva</Button>
          </div>
        </div>
      )}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="audit-detail-dialog">
          <DialogHeader><DialogTitle className="font-mono text-base">{detail?.action}</DialogTitle></DialogHeader>
          <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-slate-100">{JSON.stringify(detail, null, 2)}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
